import { schemas } from '#oaf_schema.registry';
import { createStore, createWireStore, selectUrl } from '@oneappexchange/lwc-wired';
import { IS_DEVELOPMENT } from '~/config';
import { getHasJapaneseListings } from '~/modules/services/listingsService';
import { getPublisherAccountDetails } from '~/modules/services/pricing/pricingService';
import { handleException } from '~/modules/utilities/errorUtilities';
import { logger } from '~/modules/utilities/logger';
import { notifyFailure } from '~/modules/utilities/notificationUtilities';
import * as publisherService from '../../services/publisherService';
import { i18nStore } from '../i18nStore';
import { PUBLISHER_ROUTE, routerStore } from '../routerStore';
const initialState = {
  isLoading: false,
  hasJapaneseListings: false,
  publisher: null,
  // TODO
  // This state is not related to the publisher object
  // It should live in it's own store
  commercePublisher: {
    hasLoadedFromBT: false,
    isLoadingStripeData: false,
    stripeConnectUrl: '',
    stripeSignUpStatus: 'None',
    stripeAccount: ''
  }
};
export const publisherStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  setHasJapaneseListings(hasJapaneseListings) {
    set(function setHasJapaneseListings(draft) {
      draft.hasJapaneseListings = hasJapaneseListings;
    });
  },
  setStripeConnectUrl(connectUrl) {
    set(function setStripeConnectUrl(draft) {
      draft.commercePublisher.stripeConnectUrl = connectUrl;
    });
  },
  setPublisher(publisher) {
    set(function setPublisher(draft) {
      draft.publisher = publisher;
      // Ensure publisher has the required plugins / extensions
      selectLogoSet(draft, true);
    });
  },
  async fetchPublisher() {
    set(function setIsLoading(draft) {
      draft.isLoading = true;
    });
    try {
      const [hasJapaneseListings, publisher] = await Promise.all([getHasJapaneseListings(), publisherService.getOrCreatePublisher()]);
      set(function fetchPublisher(draft) {
        draft.publisher = publisher;
        draft.hasJapaneseListings = hasJapaneseListings;
      });
      set(function finishedLoading(draft) {
        draft.isLoading = false;
      });
    } catch (error) {
      handleException(error, 'publisherStore.fetchPublisher');
      this.reset();
    }
    return get().publisher;
  },
  upsertPlugin(plugin, draft) {
    if (draft) {
      const index = draft.publisher.plugins.findIndex(test => test.pluginType === plugin.pluginType);
      if (index === -1) {
        draft.publisher.plugins.push(plugin);
      } else {
        draft.publisher.plugins[index].data = plugin.data;
      }
    }
    return plugin;
  },
  upsertExtension(extension, draft) {
    if (draft) {
      var _draft$publisher, _draft$publisher$exte;
      const index = (_draft$publisher = draft.publisher) === null || _draft$publisher === void 0 ? void 0 : (_draft$publisher$exte = _draft$publisher.extensions) === null || _draft$publisher$exte === void 0 ? void 0 : _draft$publisher$exte.findIndex(({
        extensionType
      }) => extensionType === extension.extensionType);
      if (index === -1) {
        draft.publisher.extensions.push(extension);
      } else {
        draft.publisher.extensions[index].data = extension.data;
      }
    }
    return extension;
  },
  async setStripeSignUpStatus() {
    set(function setLoadingStripeData(draft) {
      draft.commercePublisher.isLoadingStripeData = true;
    });
    try {
      const publisherAccountDetails = await getPublisherAccountDetails();
      if (publisherAccountDetails) {
        set(function setStripeSignUpStatus(draft) {
          draft.commercePublisher.stripeSignUpStatus = publisherAccountDetails.status || 'None';
          draft.commercePublisher.stripeAccount = publisherAccountDetails.stripeAccountId;
          draft.commercePublisher.hasLoadedFromBT = true;
        });
      }
    } catch (error) {
      logger.error(error);
      notifyFailure(i18nStore.getState().i18nStaticCache.pricing.pricingErrors.stripe_connection_error);
    } finally {
      set(function setEndLoadingStripeData(draft) {
        draft.commercePublisher.isLoadingStripeData = false;
      });
    }
  },
  setPublisherCountry(country) {
    set(function setPublisherCountry(draft) {
      draft.publisher.country = country;
      if (country === i18nStore.getState().i18nStaticCache.pricing.tax.countries.US) {
        selectForcePublisher(draft, true).publisherTaxes = [];
      }
    });
  },
  setPublisherTaxes(publisherTaxes) {
    set(function setPublisherTaxes(draft) {
      selectForcePublisher(draft, true).publisherTaxes = publisherTaxes;
    });
  },
  getPartnerAggreement() {
    try {
      return publisherService.getPartnerAggreement();
    } catch (error) {
      handleException(error, 'publisherStore.getPartnerAggreement');
      notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
    }
    return null;
  },
  async postPartnerAggreementAccepted() {
    try {
      const response = await publisherService.postPartnerAggreementAccepted();
      if (response) {
        set(function setISVClickthrough(draft) {
          const draftPublisher = selectForcePublisher(draft, true);
          const {
            ISVClickthroughName,
            ISVClickthroughDateTime
          } = response.extensions[0].data;
          Object.assign(draftPublisher, {
            ISVClickthroughName,
            ISVClickthroughDateTime
          });
        });
        return true;
      }
    } catch (error) {
      handleException(error, 'publisherStore.postPartnerAggreementAccepted');
      notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
      return false;
    }
    return false;
  }
}), 'PUBLISHER', IS_DEVELOPMENT);

// Initialize the store only when we have a publisher and we're in the right page
async function checkShouldInitialize() {
  const {
    url
  } = routerStore.getState();
  if (!url || !PUBLISHER_ROUTE.includes(url)) {
    return;
  }
  publisherStore.setHasJapaneseListings(await getHasJapaneseListings());
}
routerStore.subscribe(checkShouldInitialize, selectUrl);
export function selectPublisher(state) {
  return state.publisher;
}
export function selectJapanesePublisher(state) {
  var _state$publisher, _state$publisher$mark;
  return (_state$publisher = state.publisher) === null || _state$publisher === void 0 ? void 0 : (_state$publisher$mark = _state$publisher.marketplaceLocalization) === null || _state$publisher$mark === void 0 ? void 0 : _state$publisher$mark.jp;
}
export function selectHasJapaneseListings(state) {
  return state.hasJapaneseListings;
}
export function selectPublisherId(state) {
  var _state$publisher2;
  return (_state$publisher2 = state.publisher) === null || _state$publisher2 === void 0 ? void 0 : _state$publisher2.external_id;
}
export function selectCommercePublisher(state) {
  return state.commercePublisher;
}
export function selectPublisherTaxes(state) {
  return selectForcePublisher(state).publisherTaxes || [];
}
export function selectPublisherCountryTaxes(state) {
  var _state$publisher3;
  return {
    country: (_state$publisher3 = state.publisher) === null || _state$publisher3 === void 0 ? void 0 : _state$publisher3.country,
    publisherTaxes: state.publisher ? selectPublisherTaxes(state) : undefined
  };
}
export function selectLogoSet(state, isDraftState) {
  var _state$publisher4;
  const pluginType = 'publisher/plugins/PublisherLogo';
  let plugin = (_state$publisher4 = state.publisher) === null || _state$publisher4 === void 0 ? void 0 : _state$publisher4.plugins.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = publisherStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? state : undefined);
  }
  return plugin.data;
}
export function selectLogoSetItems(state, isDraftState) {
  return selectLogoSet(state, isDraftState).items;
}
export function selectISVClickthrough(state) {
  var _forcePublisher$ISVCl, _forcePublisher$ISVCl2;
  const forcePublisher = selectForcePublisher(state);
  return {
    ISVClickthroughName: (_forcePublisher$ISVCl = forcePublisher.ISVClickthroughName) !== null && _forcePublisher$ISVCl !== void 0 ? _forcePublisher$ISVCl : '',
    ISVClickthroughDateTime: (_forcePublisher$ISVCl2 = forcePublisher.ISVClickthroughDateTime) !== null && _forcePublisher$ISVCl2 !== void 0 ? _forcePublisher$ISVCl2 : ''
  };
}
export function selectForcePublisher(state, isDraftState) {
  var _state$publisher5, _state$publisher5$ext;
  const extensionType = 'publisher/extensions/force/Publisher';
  let publisherForce = (_state$publisher5 = state.publisher) === null || _state$publisher5 === void 0 ? void 0 : (_state$publisher5$ext = _state$publisher5.extensions) === null || _state$publisher5$ext === void 0 ? void 0 : _state$publisher5$ext.find(test => test.extensionType === extensionType);
  if (!publisherForce) {
    publisherForce = publisherStore.upsertExtension({
      extensionType,
      data: schemas[extensionType].new()
    }, isDraftState ? state : undefined);
  }
  return publisherForce.data;
}
export const WirePublisherStore = createWireStore(publisherStore);